import { useCookies } from '@/components/composable/composable_cookie'
import router from '@/router'
import { parse_jwt } from '@/service/service_security'
import type { components, paths } from '@/types/hypervision_schema'
import createClient from 'openapi-fetch'
import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { toast } from 'vue-sonner'

const { getCookieValue, deleteCookieValue, setCookieValue } = useCookies()
type GatewayStoreState = {
  user: components['schemas']['User'] | null
  jwt: Ref<string | null>
  authenticated: boolean
}

const client = createClient<paths>({
  baseUrl: '/api'
})

export const useSecurityStore = defineStore('security', {
  state: (): GatewayStoreState => ({
    user: null,
    jwt: getCookieValue('hv_jwt'),
    authenticated: false
  }),
  getters: {
    getUser: (state) => state.user,
    getJWT: (state) => state.jwt,
    isAuthenticated: (state): boolean => {
      if (state.jwt === null) return false
      const payload = parse_jwt(state.jwt)
      const expiration_date = new Date(payload.exp * 1000)
      const current_date = new Date()

      if (expiration_date.getTime() < current_date.getTime()) {
        return false
      }

      return true
    }
  },
  actions: {
    /**
     * Authenticate to the hypervision REST API.
     * Sets the hv_jwt cookie if successful.
     * @param login
     * @param password
     */
    authenticate(tenantCode: string, login: string, password: string, redirect?: string) {
      const response = client.POST('/api/GetToken', {
        parseAs: 'text',
        params: {
          query: {
            tenantCode,
            login,
            password
          }
        }
      })

      response.then((response) => {
        if (response.data) {
          const cookie_value = parse_jwt(response.data)
          setCookieValue('hv_jwt', response.data, new Date(cookie_value.exp * 1000))
          this.jwt = getCookieValue('hv_jwt').value
        } else {
          const error = response.error as unknown as RequestError
          if (error.status === 401) {
            toast.error('Identifiants invalides')
          }
        }
      })
      return response
    },
    /**
     * Logout the current authenticated user
     * clear the authentication cookie and redirect to
     * the autentication page
     */
    logout() {
      if (this.isAuthenticated) {
        deleteCookieValue('hv_jwt')
        this.jwt = null
        window.location.href = '/'
      }
    },
    /**
     * Fetch logged in user data
     * @returns
     */
    fetchUser() {
      const request = client.GET('/api/Users/WhoAmi', {
        headers: {
          Authorization: `Bearer ${this.getJWT}`
        }
      })

      request.then((response) => {
        if (response.data !== undefined) {
          this.user = response.data
        }
      })

      return request
    }
  }
})
